import React from 'react';
import { Button } from '@material-ui/core';
import { withStyles, withTheme } from '@material-ui/core/styles';
import { compose } from 'recompose';
import MapView, {
  Marker,
  Popup,
  MapControls,
  MapConstant,
  SingleSearch
} from '../../../../lib/Map';
import { CustomerLocationMarker, TerminalMarker } from '../../../mapComponents/Markers';
import { ZonePopUpInfo } from '../../../mapComponents/Popups';
import { postData } from '../../../../api/componentAPIs';
import { withSnackbar } from 'notistack';
import Tooltip from '../../../common/StyledToolTip';
import { GEOCODE_OPTIONS } from './utils';
import { withAppConfig } from '../../../../appConfigContext';
import GeocoderSearch from '../GeocoderSearchBox';
import { isHighAddressConfidenceLevel } from '../../../../lib/Map/Trimble/utils/SingleSearchService';
import styles from './styles';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

const LOCATION_UPDATE_STATUS = {
  PENDING: 0,
  SUCCESS: 1,
  FAILURE: 2
};

class GeocodingModal extends React.Component {
  constructor(props) {
    super(props);
    this.terminalLocation = this.props.userData?.terminal?.location; 
    this.state = {
      zoneInfo: null,
      updatedLocation: null,
      showCustomerLabel: true,
      locationUpdateStatus: LOCATION_UPDATE_STATUS.PENDING,
      address: '',
      isHighAddressConfidence: true
    };
  }

  componentDidMount() {
    this.geocodeLocation(this.props.customer);
  }

  getLocationDetails = ({ longitude, latitude, address }) => {
    if (longitude && latitude && address) {
      this.setState({
        address,
        updatedLocation: {
          lng: longitude,
          lat: latitude
        },
        showCustomerLabel: true
      });
      return;
    }
    this.setState({ address: '' });
  };

  onMapClicked = () => {
    this.setState({
      zoneInfo: null,
      showCustomerLabel: true
    });
  };

  onZoneClicked = (zoneInfo) => {
    this.setState({
      zoneInfo,
      showCustomerLabel: true
    });
  };

  onCustomerLocationUpdated = ({ position }) => {
    this.setState({
      updatedLocation: {
        lng: position.lng,
        lat: position.lat
      },
      address: '',
      showCustomerLabel: true
    });
  };

  /**
   * Checks if a string contains special characters that are not allowed in address fields.
   * Allowed characters: alphanumeric, & - / , . # ( ) ' : ; and space
   * @returns true if the string contains disallowed characters, or if the string is empty/null/undefined
   */
  containsInvalidAddressChars = (str) => {
    return str ? /[^a-zA-Z0-9&\-/,.#()':; ]/.test(str) : true;
  };

  /**
   * Sanitizes an address by removing special characters (non-alphanumeric except space)
   */
  sanitizeAddressString = (address) => {
    return address ? address.replaceAll(/[^a-zA-Z0-9 ]/g, '').trim() : address;
  };

  /**
   * Combines valid address lines for geocoding/geofencing with lenient validation.
   * Unlike the old implementation which rejects addresses containing any special characters,
   * this function accepts addresses with typical address characters (like #, -, ., ', etc.)
   * then sanitizes the output by removing those special characters.
   *
   * This allows addresses like "1730 Clearwater Road #2" to be processed,
   * resulting in sanitized output "1730 Clearwater Road 2".
   */
  combineValidAddressLinesForGeocoding = (address, address2) => {
    let result;
    if (address && !this.containsInvalidAddressChars(address)) {
      result = this.sanitizeAddressString(address);
    }
    if (address2 && !this.containsInvalidAddressChars(address2)) {
      const sanitizedAddress2 = this.sanitizeAddressString(address2);
      result = result ? result + ', ' + sanitizedAddress2 : sanitizedAddress2;
    }
    return result || '';
  };

  getFullAddress = ({address='', address2='', city='', state='', zip=''} = {}) => {
    return [address, address2, city, state, zip]
    .filter(Boolean)
    .join(',');
  };

  geocodeLocation = (customer) => {
    const sanitizedAddress = this.combineValidAddressLinesForGeocoding(customer.address, customer.address2);
    const searchedAddress = this.getFullAddress({address: sanitizedAddress, city: customer.city, state: customer.state, zip: customer.zip});
    this.fullAddress = this.getFullAddress(customer);
    if (customer.isGeoCoded) {
      this.setState({
        updatedLocation: {
          lng: customer.longitude,
          lat: customer.latitude
        },
        locationUpdateStatus: LOCATION_UPDATE_STATUS.SUCCESS
      });
      return;
    }

    
    SingleSearch(searchedAddress).then((data) => {

      if (data && data.Locations.length > 0) {
        const isHighAddressConfidence = isHighAddressConfidenceLevel(data.QueryConfidence);
        this.setState({
          //If the QueryConfidence value is "Very High", "Good, or "Uncertain match" then only update the marker location on the geocoding popup. Otherwise, use the location sent by the API.
          updatedLocation: {
            lng: isHighAddressConfidence ? data.Locations[0].Coords.Lon : customer.longitude,
            lat: isHighAddressConfidence ? data.Locations[0].Coords.Lat : customer.latitude
          },
          locationUpdateStatus: LOCATION_UPDATE_STATUS.SUCCESS,
          isHighAddressConfidence
        });
        !isHighAddressConfidence &&
          this.props.enqueueSnackbar('Geocoding Failed', {
            variant: 'error'
          });
        return;
      }
      this.props.enqueueSnackbar(`Failed to geocode, please search the address`, {
        variant: 'error'
      });
      this.onGeocodingFailure(customer);
    });
  };

  onGeocodingFailure = (customer) => {
    this.setState({
      updatedLocation: {
        lat: customer.latitude,
        lng: customer.longitude
      },
      locationUpdateStatus: LOCATION_UPDATE_STATUS.FAILURE
    });
  };

  onCustomerMarkerDragged = () => {
    if (this.state.showCustomerLabel) {
      this.setState({
        showCustomerLabel: false,
        locationUpdateStatus: LOCATION_UPDATE_STATUS.SUCCESS
      });
    }
  };

  saveCustomerLocation = async () => {
    if (this.state.updateInProgress) {
      return;
    }

    this.setState({
      updateInProgress: true
    });

    const geocodingOption = GEOCODE_OPTIONS[this.props.customer.type];
    if (!geocodingOption) {
      this.onSaveCustomerLocationFailure();
      return;
    }

    const apiName = geocodingOption.apiName;
    const requestParams = await geocodingOption.getRequestParams?.(
      this.props.customer,
      this.state.updatedLocation,
      this.props.userData
    );

    postData(apiName, requestParams).then(
      (response) => {
        if (response && Math.trunc(response.status / 100) === 2) {
          this.props.enqueueSnackbar(
            `${this.props.customer.pickupId ? 'Shipper' : 'Customer'} location updated`,
            {
              variant: 'success'
            }
          );
          this.props.close(true);
        } else {
          this.onSaveCustomerLocationFailure(response.status);
        }
      },
      () => {
        this.onSaveCustomerLocationFailure();
      }
    );
  };

  onSaveCustomerLocationFailure = (statusCode) => {
    this.setState({
      updateInProgress: false
    });
    if (statusCode === 403) {
      return;
    }
    const errorMsg = `Failed to update  ${
      this.props.customer.pickupId ? 'shipper' : 'customer'
    } location`;
    this.props.enqueueSnackbar(errorMsg, { variant: 'error' });
  };

  getCustomerTooltip = () => {
    let message = '';
    const { classes } = this.props;

    switch (this.state.locationUpdateStatus) {
      case LOCATION_UPDATE_STATUS.SUCCESS:
        message = 'Updated location';
        break;
      case LOCATION_UPDATE_STATUS.FAILURE:
        message = 'Unable to update';
        break;
      default:
        break;
    }

    return (
      <React.Fragment>
        <div className={classes.locationUpdated}>{message}</div>
        <div>Drag to re-position</div>
      </React.Fragment>
    );
  };

  getLocationUpdateStatus = () => {
    switch (this.state.locationUpdateStatus) {
      case LOCATION_UPDATE_STATUS.PENDING:
        return <span>{` ${this.props.customer.name} update is in progress`}</span>;
      case LOCATION_UPDATE_STATUS.SUCCESS:
        return <span>{` ${this.props.customer.name} was updated`}</span>;
      case LOCATION_UPDATE_STATUS.FAILURE:
      default:
        return (
          <span className={this.props.classes.failMessage}>
            {` Could not update ${this.props.customer.name} location`}
          </span>
        );
    }
  };

  render() {
    const { theme, classes, customer } = this.props;
    const { updatedLocation, isHighAddressConfidence } = this.state;
    return (
      <div className={classes.dialog}>
        <div>
          <div className={classes.dialogTitle}>
            <span>
              Correct Location:
              {this.getLocationUpdateStatus()}
            </span>
          </div>
          <div className={classes.addressContainer}>
            <div className={classes.message}>
              <span>{`Freight Bill Address: ${this.fullAddress}`}</span>
              <br />
              {customer.isMiscellaneousCustomer ? (
                <span>Customer#: Miscellaneous</span>
              ) : (
                <span>{`Customer#: ${customer.number}`}</span>
              )}

              {this.state.locationUpdateStatus === LOCATION_UPDATE_STATUS.SUCCESS && (
                <div>
                  <span>Would you like to save the new location?</span>
                  <br />
                  <span>
                    If marker is not correctly placed on the map, please drag to re-position
                  </span>
                </div>
              )}
              {this.state.locationUpdateStatus === LOCATION_UPDATE_STATUS.FAILURE && (
                <div>
                  Adjust the marker on the map (or) search for a nearby address to update the
                  location
                </div>
              )}
            </div>
            <div className={classes.searchContainer}>
              <GeocoderSearch
                getLocationDetails={this.getLocationDetails}
                inputValue={this.state.address}
                geocoderTheme={theme.themeName}
              />
              {!isHighAddressConfidence && (
                <div className={classes.lowConfidenceAddressContainer}>
                  <ErrorOutlineIcon />
                  <span className={classes.lowConfidenceAddressLabel}>
                    Not able to geocode, please Copy/Paste the lat,longs (or) search for nearby
                    addresses using the search box.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={classes.dialogContent}>
          {updatedLocation ? (
            <MapView
              zoom={18}
              onClick={this.onMapClicked}
              mapId={'geoCodingMap'}
              satteliteView
              mapTheme={theme.themeName}
              center={updatedLocation}
              mapConfig={this.props.configJSON?.maps}>
              <MapControls
                controlOptions={{
                  zoomControl: {
                    position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT
                  },
                  satelliteViewControl: {
                    position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT
                  },
                  reCenterMapControl: {
                    position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT
                  },
                  showZones: {
                    position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
                    terminalID: this.props.userData.terminal?.code,
                    geoJson: this.props.userData.geoJson,
                    onZoneClicked: this.onZoneClicked
                  }
                }}
              />
              <Marker
                data={customer}
                zIndex={2}
                position={updatedLocation}
                key={customer.id}
                draggable={true}
                onDrag={this.onCustomerMarkerDragged}
                onDragEnd={this.onCustomerLocationUpdated}
                forceUpdate={true}
                isActiveShipment={true}>
                <Tooltip
                  title={this.state.showCustomerLabel ? this.getCustomerTooltip() : ''}
                  open
                  placement="top"
                  PopperProps={{ disablePortal: true }}>
                  <CustomerLocationMarker backgroundColor={theme.gradientSecondaryColor} />
                </Tooltip>
              </Marker>
              <Marker
                position={this.terminalLocation}
                animation
                // anchor point is the point in the image which should overlap the location,
                // Terminal image is of different size than the marker so we can override the default value as shown below
                anchorPoint={{ x: 20, y: 20 }}
                zIndex={1}
                forceUpdate={true}
                >
                <TerminalMarker
                  data={this.props.userData?.terminal}
                  color={theme.terminalContentColor}
                />
              </Marker>

              {this.state.zoneInfo && (
                <Popup isCloseButton position={this.state.zoneInfo.position}>
                  <ZonePopUpInfo data={this.state.zoneInfo.data} />
                </Popup>
              )}
            </MapView>
          ) : (
            <div className={classes.geoCodingProgress}>Location update is in progress</div>
          )}
        </div>
        <div className={classes.dialogAction}>
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            classes={{ root: classes.button, disabled: classes.disabled }}
            onClick={() => this.props.close()}
            disabled={this.state.updateInProgress}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            classes={{ root: classes.button, disabled: classes.disabled }}
            onClick={this.saveCustomerLocation}
            disabled={!this.state.updatedLocation}>
            {this.state.updateInProgress ? 'Saving.....' : 'Save location'}
          </Button>
        </div>
      </div>
    );
  }
}

export default compose(
  withStyles(styles),
  withTheme(),
  withSnackbar,
  withAppConfig
)(GeocodingModal);
